import type { Debt, StrategyName } from '@/types'
import { runSimulation } from './simulation'
import { MAX_SIMULATION_MONTHS } from '@/lib/constants'
import { SIMULATION_CLEARED_THRESHOLD } from './tolerances'

/**
 * Per-debt clearance milestone derived from a strategy simulation.
 *
 * The Path to Freedom module turns the abstract payoff simulation into a
 * narrative: each debt becomes a named win with a projected date. This is
 * a pure derivation — no new persistence, no new entities — so the
 * milestones stay in lockstep with whatever the user sees on the Strategy
 * Engine page.
 *
 * State semantics:
 *   - `'projected'` — under the current strategy + budget, this debt's
 *     balance hits zero at `clearedAtDate`. The 99% case.
 *   - `'never_converges'` — the simulation horizon ends with this debt
 *     still owing balance. Typically happens on `minimum_only` when a
 *     lender's minimum payment is less than the monthly interest accrual
 *     (the balance grows month over month). The UI surfaces this as an
 *     amber "Won't clear" chip rather than silently dropping the row.
 *
 * v1 deliberately omits a `'cleared'` past state — we don't yet track
 * historically-cleared debts as a separate entity. When a user marks a
 * debt paid off, they either zero its balance (where the engine's
 * `eligible` filter at `simulation.ts:14` then excludes it from
 * milestones) or delete it from the registry entirely. A future iteration
 * may add a cleared-debt archive that lets us render past wins as
 * full-state milestones.
 */
export interface Milestone {
  debtId: string
  debtName: string
  /** Balance at the simulation start (debt.balance at the time of derivation). */
  startingBalance: number
  /** Effective APR at start — used for severity colour. */
  apr: number
  state: 'projected' | 'never_converges'
  /** 0-indexed month offset from `fromDate`. Undefined when never_converges. */
  clearedAtMonthIndex?: number
  /** ISO `YYYY-MM`. Undefined when never_converges. */
  clearedAtDate?: string
}

/**
 * Derives the chronological clearance sequence for the user's
 * optimisation debts under the chosen strategy + extra-payment lever.
 *
 * Reuses {@link runSimulation} directly so the dates this function
 * produces are guaranteed to be the same dates the Strategy Engine
 * page shows in its comparison chart — a single source of truth.
 *
 * Returns `[]` if there are no eligible debts. Otherwise returns one
 * Milestone per eligible debt, sorted in chronological order
 * (`'projected'` ascending by `clearedAtMonthIndex`, with
 * `'never_converges'` entries last, alphabetised by name for stability).
 */
export function pathToFreedom(
  debts: Debt[],
  strategy: StrategyName,
  extraMonthlyPayment: number,
  fromDate: Date = new Date()
): Milestone[] {
  // Mirror simulation.ts's eligibility filter so a debt that the simulation
  // ignores doesn't sneak into the milestone list as a never-converges row.
  const eligible = debts.filter((d) => d.isInOptimizationEngine && d.balance > 0)
  if (eligible.length === 0) return []

  const totalMinimums = eligible.reduce((s, d) => s + d.minimumPayment, 0)
  const totalBudget = totalMinimums + extraMonthlyPayment

  const result = runSimulation(eligible, totalBudget, strategy, fromDate)

  const milestones: Milestone[] = eligible.map((debt) => {
    const cleared = findClearedSnapshot(result.monthlySnapshots, debt.id)
    if (cleared) {
      return {
        debtId: debt.id,
        debtName: debt.name,
        startingBalance: debt.balance,
        apr: debt.apr,
        state: 'projected',
        clearedAtMonthIndex: cleared.month - 1, // monthly snapshots are 1-indexed
        clearedAtDate: cleared.date,
      }
    }
    return {
      debtId: debt.id,
      debtName: debt.name,
      startingBalance: debt.balance,
      apr: debt.apr,
      state: 'never_converges',
    }
  })

  // Sort: projected first by month-index ascending, then never-converges by
  // debt name (lexicographic) for deterministic ordering across renders.
  //
  // **Tie-break is strategy-aware** (v0.8.20). When multiple debts clear
  // in the same month — common under snowball with a real extra payment,
  // because a single cascade can zero several small balances inside one
  // month — the JS stable-sort would otherwise preserve the `eligible`
  // filter's input order, which is registry order. That made the timeline
  // surface registry order at the top instead of the strategy's intended
  // priority (e.g. snowball user expects smallest-balance #1, but a
  // higher-balance debt earlier in the registry would show #1 instead).
  // The tie-break below makes ties resolve in strategy intent.
  return milestones.sort((a, b) => {
    if (a.state === 'projected' && b.state === 'projected') {
      const monthDiff = (a.clearedAtMonthIndex ?? 0) - (b.clearedAtMonthIndex ?? 0)
      if (monthDiff !== 0) return monthDiff
      return strategyTieBreak(a, b, strategy)
    }
    if (a.state === 'projected') return -1
    if (b.state === 'projected') return 1
    return a.debtName.localeCompare(b.debtName)
  })
}

/**
 * Resolves clearance-month ties between two milestones using the active
 * strategy's priority signal. Mirrors `sortByStrategy` in `strategies.ts`:
 *
 *   - snowball → smaller balance first
 *   - avalanche / minimum_only → higher APR first
 *   - hybrid → higher (APR × balance) first
 */
function strategyTieBreak(a: Milestone, b: Milestone, strategy: StrategyName): number {
  switch (strategy) {
    case 'snowball':
      return a.startingBalance - b.startingBalance
    case 'avalanche':
    case 'minimum_only':
      return b.apr - a.apr
    case 'hybrid':
      return b.apr * b.startingBalance - a.apr * a.startingBalance
  }
}

/**
 * Total projected months until every optimisation debt clears, given the
 * full milestone list. Returns `null` if any debt is `never_converges` —
 * the journey isn't fully chartable without an answer for every debt.
 */
export function totalMonthsToFreedom(milestones: Milestone[]): number | null {
  if (milestones.length === 0) return 0
  const projected = milestones.filter((m) => m.state === 'projected')
  if (projected.length !== milestones.length) return null
  // Last cleared milestone marks "debt-free." `clearedAtMonthIndex` is the
  // 0-indexed month, so the answer is `index + 1` (month count).
  return Math.max(...projected.map((m) => (m.clearedAtMonthIndex ?? 0) + 1))
}

// ─── Helpers ────────────────────────────────────────────────────────────

interface SimSnapshot {
  month: number
  date: string
  debtBalances: Record<string, number>
}

/**
 * Walks the monthly snapshots and returns the FIRST one where the named
 * debt's balance has hit the cleared threshold (< 1¢, matching the
 * convergence-check in `simulation.ts:43`).
 */
function findClearedSnapshot(snapshots: SimSnapshot[], debtId: string): SimSnapshot | undefined {
  for (const snap of snapshots) {
    const bal = snap.debtBalances[debtId]
    if (bal !== undefined && bal < SIMULATION_CLEARED_THRESHOLD) return snap
  }
  return undefined
}

// Re-export so consumers can constrain their UI rendering to known horizons.
export { MAX_SIMULATION_MONTHS }
